import { supabase } from './client';

export class SupabaseService {
  /**
   * Test Supabase connection
   */
  static async testConnection() {
    try {
      // Simple query to test connection
      const { data, error } = await supabase.from('personal_info').select('id').limit(1);
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned, which is OK for a test
        console.error('Supabase connection test failed:', error);
        return false;
      }
      return true;
    } catch (error: any) {
      console.error('Supabase connection test error:', error);
      if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
        console.error('❌ CORS Error Detected. Please check:');
        console.error('1. .env file has correct VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
        console.error('2. Development server has been restarted after updating .env');
        console.error('3. Browser cache cleared');
        console.error('4. See SUPABASE_CORS_TROUBLESHOOTING.md for detailed steps');
      }
      return false;
    }
  }

  static async getById<T>(table: string, id: string) {
    try {
      const { data, error } = await supabase.from(table).select('*').eq('id', id).single();
      if (error) {
        console.error(`Supabase getById error for table "${table}":`, error);
        throw error;
      }
      return data as T;
    } catch (error: any) {
      if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
        console.error('❌ CORS Error: See SUPABASE_CORS_TROUBLESHOOTING.md');
      }
      throw error;
    }
  }

  static async getMany<T>(table: string, filters: Record<string, any> = {}, options: { order?: { column: string; ascending?: boolean }; limit?: number } = {}) {
    try {
      let query = supabase.from(table).select('*');
      Object.entries(filters).forEach(([column, value]) => {
        query = query.eq(column, value);
      });
      if (options.order) {
        query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
      }
      if (options.limit) {
        query = query.limit(options.limit);
      }
      const { data, error } = await query;
      if (error) {
        console.error(`Supabase getMany error for table "${table}":`, error);
        throw error;
      }
      return (data as unknown) as T[];
    } catch (error: any) {
      if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
        console.error('❌ CORS Error: See SUPABASE_CORS_TROUBLESHOOTING.md');
      }
      throw error;
    }
  }

  static async insert<T>(table: string, payload: Partial<T>) {
    const { data, error } = await supabase.from(table).insert(payload).select('*').single();
    if (error) throw error;
    return data as T;
  }

  static async update<T>(table: string, id: string, payload: Partial<T>) {
    const { data, error } = await supabase.from(table).update(payload).eq('id', id).select('*').single();
    if (error) throw error;
    return data as T;
  }

  static async delete(table: string, id: string) {
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) throw error;
    return true;
  }

  static async upsert<T>(table: string, payload: Partial<T>) {
    const { data, error } = await supabase.from(table).upsert(payload).select('*').single();
    if (error) throw error;
    return data as T;
  }
}

// Specific services mapped from Firebase collections to Supabase tables
export const ApplicationFormService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('application_forms', { user_id }, { order: { column: 'updated_at', ascending: false }, limit: 1 });
  },
  async upsert(user_id: string, data: any, existingId?: string) {
    if (existingId) {
      return SupabaseService.update('application_forms', existingId, { user_id, ...data });
    }
    return SupabaseService.insert('application_forms', { user_id, ...data });
  },
  async getById(id: string) {
    return SupabaseService.getById('application_forms', id);
  }
};

export const DocumentService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('documents', { user_id });
  },
  async add(data: any) {
    return SupabaseService.insert('documents', data);
  },
  async update(id: string, data: any) {
    return SupabaseService.update('documents', id, data);
  },
  async getById(id: string) {
    return SupabaseService.getById('documents', id);
  },
  async delete(id: string) {
    return SupabaseService.delete('documents', id);
  }
};

export const PersonalInfoService = {
  async getByUserId(user_id: string) {
    const list = await SupabaseService.getMany('personal_info', { user_id }, { limit: 1 });
    return list;
  },
  async upsert(user_id: string, data: any, existingId?: string) {
    if (existingId) {
      return SupabaseService.update('personal_info', existingId, { user_id, ...data });
    }
    return SupabaseService.insert('personal_info', { user_id, ...data });
  },
  async updateProfileImage(user_id: string, imageData: {
    profileImageUrl?: string | null;
    profileImageThumbnailUrl?: string | null;
    profileImagePath?: string | null;
    profileImageThumbnailPath?: string | null;
  }) {
    const existing = await this.getByUserId(user_id);
    const existingId = existing.length > 0 ? (existing[0] as any).id : undefined;
    
    // Convert camelCase to snake_case for database
    const dbData = {
      profile_image_url: imageData.profileImageUrl,
      profile_image_thumbnail_url: imageData.profileImageThumbnailUrl,
      profile_image_path: imageData.profileImagePath,
      profile_image_thumbnail_path: imageData.profileImageThumbnailPath
    };
    
    return this.upsert(user_id, dbData, existingId);
  }
};

export const ConsultationService = {
  async add(data: any) {
    return SupabaseService.insert('consultations', data);
  },
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('consultations', { user_id }, { order: { column: 'created_at', ascending: false } });
  }
};

export const AppointmentService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('appointments', { user_id }, { order: { column: 'appointment_date', ascending: false } });
  },
  async add(data: any) {
    return SupabaseService.insert('appointments', data);
  },
  async update(id: string, data: any) {
    return SupabaseService.update('appointments', id, data);
  }
};

export const PaymentService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('payments', { user_id }, { order: { column: 'created_at', ascending: false } });
  },
  async add(data: any) {
    return SupabaseService.insert('payments', data);
  },
  async update(id: string, data: any) {
    return SupabaseService.update('payments', id, data);
  }
};

// Notifications
export const NotificationService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('notifications', { user_id }, { order: { column: 'sent_at', ascending: false } });
  },
  async add(data: any) {
    return SupabaseService.insert('notifications', data);
  },
  async markAsRead(id: string) {
    return SupabaseService.update('notifications', id, { read: true });
  }
};

// Contact messages (optional, used by Resources/Contact pages)
export const ContactMessageService = {
  async add(data: any) {
    return SupabaseService.insert('contact_messages', data);
  },
  async getAll() {
    return SupabaseService.getMany('contact_messages', {}, { order: { column: 'created_at', ascending: false } });
  }
};

// User packages (tracks current user package)
export const UserPackageService = {
  async getByUserId(user_id: string) {
    const packages = await SupabaseService.getMany('user_packages', { user_id }, { limit: 1 });
    return packages.length > 0 ? packages[0] : null;
  },
  async upsert(user_id: string, package_type: string, status: string = 'active') {
    const existing = await this.getByUserId(user_id);
    const data = {
      user_id,
      package_type,
      status,
      updated_at: new Date().toISOString()
    };
    
    if (existing) {
      return SupabaseService.update('user_packages', existing.id, data);
    } else {
      return SupabaseService.insert('user_packages', data);
    }
  },
  async update(user_id: string, package_type: string, status: string = 'active') {
    const existing = await this.getByUserId(user_id);
    if (!existing) {
      throw new Error('User package not found');
    }
    
    return SupabaseService.update('user_packages', existing.id, {
      package_type,
      status,
      updated_at: new Date().toISOString()
    });
  }
};

// User services (tracks selected services)
export const UserServiceService = {
  async getByUserId(user_id: string) {
    return SupabaseService.getMany('user_services', { user_id }, { order: { column: 'selected_at', ascending: false } });
  },
  async add(user_id: string, service_id: string, service_name: string, service_category: string) {
    return SupabaseService.insert('user_services', {
      user_id,
      service_id,
      service_name,
      service_category
    });
  },
  async deleteByUserId(user_id: string) {
    const services = await this.getByUserId(user_id);
    const deletePromises = services.map(service => 
      SupabaseService.delete('user_services', service.id)
    );
    await Promise.all(deletePromises);
    return true;
  },
  async upsertServices(user_id: string, services: Array<{id: string, name: string, category: string}>) {
    // Delete existing services for user
    await this.deleteByUserId(user_id);
    
    // Add new services
    const addPromises = services.map(service => 
      this.add(user_id, service.id, service.name, service.category)
    );
    await Promise.all(addPromises);
    return true;
  }
};


